<template>
  <PTransparentLoader :loading="loading">
    <template v-if="checkin">
      <DateOfStayModal :end_of_stay="dateOfStay.end" :start_of_stay="dateOfStay.start" ref="modalDateOfStay"/>
      <AssignCouponModal
          v-if="canAssignCoupon"
          ref="modalAssignCoupon"
          :user-id="checkin.user"
          :pica-service-id="picaServiceId"
          :picacode="checkin.pica_code"
      />
      <CCard>
        <CCardHeader>
          <h5 class="main-header mb-1" v-translate translate-context="checkin.title">Checkin</h5>
        </CCardHeader>
        <CCardBody class="px-0">
          <CContainer fluid class="grid-stripped">
            <PFRow v-if="hasPerm('checkin.fields.pica_code')" :label="$pgettext('checkin.label','Pica Code')">
              <CBadge color="primary" class="badge-outline pica-code pica-code--sm mr-2">{{ checkin.pica_code }}</CBadge>
              <CButton color="dark" class="btn-circle" variant="outline" size="sm"
                       @click="copyPicaCode"
                       v-c-tooltip="{content: $pgettext('checkin.actions', 'Copy')}">
                <CIcon name="cipCopy" size="sm"/>
              </CButton>
            </PFRow>
            <PFRow v-if="hasPerm('checkin.fields.email')" :label="$pgettext('checkin.label','Email')">
              <router-link
                v-if="checkinUserId && hasPerm('admin.users.search')"
                :to="{ name: 'customer_success.user', params: { id: checkinUserId } }"
              >
                {{ checkin.email || '–' }}
              </router-link>
              <span v-else>{{ checkin.email || '–' }}</span>
            </PFRow>
            <PFRow v-if="hasPerm('checkin.fields.tags')" :label="$pgettext('checkin.label','Tags')">
              <template v-if="checkin.tags && checkin.tags.length">
                <CBadge v-for="tag in checkin.tags" :key="tag" shape="pill" color="primary-light no-delete mr-2 mb-1">
                  {{ tag }}
                </CBadge>
              </template>
              <span v-else>–</span>
            </PFRow>
            <PFRow v-if="hasPerm('checkin.fields.last_login')" :label="$pgettext('checkin.label','Last Login')">
              <template v-if="checkin.last_login">{{ checkin.last_login | formatDateTimeTZ(timeZone, 'L LT') }}</template>
              <span v-else>–</span>
            </PFRow>
            <PFRow v-if="hasPerm('checkin.fields.available_photos_number')" :label="$pgettext('checkin.label','Available Photos')">
              <span class="font-weight-semi-bold mr-3">{{ checkin.available_photos_number|number }}</span>
              <CLink :to="routeToSearchPhotos(checkin.pica_code)" class="mr-3"
                     v-if="hasPerm('old_search_photo.list')">
                <CIcon name="cip-search" class="mr-1"/>
                <translate translate-context="checkin.photos">Search photos</translate>
              </CLink>
              <CLink :to="routeToBrowsePhotos(checkin.pica_code)"
                     v-if="canBrowsePhoto">
                <CIcon name="cip-search" class="mr-1"/>
                <translate translate-context="checkin.photos">Browse photos</translate>
              </CLink>
            </PFRow>
            <PFRow :label="$pgettext('checkin.label','Date of Stay')">
              <CButton color="dark" class="btn-circle" variant="outline" size="sm"
                       @click="editDateOfStay"
                       v-if="hasPerm('checkin.update_checkin')"
              >
                <CIcon name="cipPencil" size="sm"></CIcon>
              </CButton>
              <div class="ml-2 d-inline" v-if="checkin.start_of_stay || checkin.end_of_stay">
                {{ checkin.start_of_stay|formatDate('LL', timeZone) }} - {{ checkin.end_of_stay|formatDate('LL', timeZone) }}
              </div>
            </PFRow>
          </CContainer>
        </CCardBody>
      </CCard>

      <!-- Order Items -->
      <CCard v-if="hasPerm('checkin.fields.order_items') && checkin.order_items && checkin.order_items.length">
        <CCardHeader>
          <h5 class="main-header mb-0" v-translate translate-context="checkin.orders">Orders</h5>
        </CCardHeader>
        <CCardBody class="p-0">
          <div
            v-for="item in checkin.order_items"
            :key="item.id"
            class="order-item-row"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <router-link
                  v-if="item.order_id"
                  :to="getOrderRoute(item)"
                  class="font-weight-medium"
                >
                  {{ item.order_number }}
                </router-link>
                <span v-else class="font-weight-medium">{{ item.order_number }}</span>
                <CBadge :color="getOrderStatusColor(item.order_status)" class="ml-2">
                  {{ item.order_status }}
                </CBadge>
                <span class="small text-muted ml-2">{{ item.medias_count }} foto</span>
              </div>
              <div class="text-right">
                <strong>{{ item.price | currency(item.currency) }}</strong>
                <div class="small text-muted" v-if="item.order_paid_at">
                  {{ item.order_paid_at | formatDateTimeTZ(timeZone, 'L LT') }}
                </div>
              </div>
            </div>
            <div v-if="item.refunds && item.refunds.length" class="mt-2">
              <div
                v-for="refund in item.refunds"
                :key="refund.id"
                class="refund-row d-flex justify-content-between align-items-center"
              >
                <div>
                  <router-link :to="getRefundRoute(refund)" class="text-danger small font-weight-medium">
                    <translate translate-context="checkin.orders">Refund</translate>
                    #{{ refund.id }}
                  </router-link>
                  <span v-if="refund.refund_type" class="small text-muted ml-2">{{ refund.refund_type }}</span>
                </div>
                <strong class="text-danger small">-{{ refund.amount | currency(item.currency) }}</strong>
              </div>
            </div>
          </div>
        </CCardBody>
      </CCard>

      <CCard v-if="canAssignCoupon">
        <CCardHeader class="d-flex justify-content-between align-items-center">
          <h5 class="main-header mb-0" v-translate translate-context="checkin.coupon.title">Coupons</h5>
          <CButton color="primary" size="sm" @click="openAssignCouponModal">
            <CIcon name="cil-plus" class="mr-1"/>
            <translate translate-context="checkin.coupon.actions">Assign Coupon</translate>
          </CButton>
        </CCardHeader>
        <CCardBody class="px-0">
          <div v-if="loadingRedemptions" class="text-center py-3">
            <CSpinner color="primary" size="sm"/>
          </div>
          <template v-else-if="redemptions.length === 0">
            <p class="text-muted text-center py-3 mb-0">
              <translate translate-context="checkin.coupon">No coupons assigned yet.</translate>
            </p>
          </template>
          <CListGroup v-else flush>
            <CListGroupItem v-for="redemption in redemptions" :key="redemption.id" class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ redemption.coupon.code }}</strong>
                <br>
                <small class="text-muted">{{ redemption.coupon.batch.promotion_rule.name }}</small>
                <template v-if="getRedemptionAmount(redemption)">
                  <br>
                  <small class="text-muted">{{ getRedemptionAmount(redemption) | currency(eventCurrency) }}</small>
                </template>
              </div>
              <div class="text-right">
                <CBadge :color="redemption.is_consumed ? 'secondary' : 'success'">
                  {{ redemption.is_consumed ? $pgettext('coupon.status', 'Used') : $pgettext('coupon.status', 'Active') }}
                </CBadge>
                <br>
                <small class="text-muted">{{ redemption.redeemed_at | formatDate('lll', timeZone) }}</small>
              </div>
            </CListGroupItem>
          </CListGroup>
        </CCardBody>
      </CCard>
    </template>
  </PTransparentLoader>
</template>
<script>
import {mapActions, mapGetters} from "vuex";
import PFRow from "@/domain/core/components/PFRow.vue";
import PTransparentLoader from "@/domain/core/components/PTransparentLoader.vue";
import DateOfStayModal from "@/domain/checkin/components/DateOfStayModal.vue";
import AssignCouponModal from "@/domain/edgeCoupon/components/AssignCouponModal.vue";
import moment from "moment";
import ConfigScope from "@/domain/core/constant/configScope";
import {getDetailRouteParams, TRANSACTION_TYPES} from "@/domain/accounting/constants";

export default {
  name: "Checkin",
  components: {DateOfStayModal, AssignCouponModal, PTransparentLoader, PFRow},
  data() {
    return {
      loading: true,
      start_of_stay: '',
      end_of_stay: '',
    }
  },
  computed: {
    ...mapGetters('event', [
      'picaServiceId',
      'timeZone',
      'isEdge',
      'eventCurrency'
    ]),
    ...mapGetters('checkin', [
      'checkin',
    ]),
    ...mapGetters('eventWizard', [
      'canSeePhotoSearch',
    ]),
    ...mapGetters('features', [
      'isCouponEnabled'
    ]),
    ...mapGetters('login', [
      'hasPerm'
    ]),
    ...mapGetters('edgeCoupon', {
      redemptions: 'redemptions',
      couponLoading: 'loading'
    }),
    loadingRedemptions() {
      return this.couponLoading?.redemptions || false
    },
    canAssignCoupon() {
      return this.isCouponEnabled && this.isEdge && this.hasPerm('checkin.coupon.assign')
    },
    canBrowsePhoto() {
      return this.canSeePhotoSearch && this.hasPerm('photo_search.filter_by_pica_code')
    },
    checkinUserId() {
      return this.checkin?.user_id || this.checkin?.user || null
    },
    dateOfStay() {
      return {
        start: moment.tz(this.checkin.start_of_stay, this.timeZone).format('YYYY-MM-DD'),
        end: moment.tz(this.checkin.end_of_stay, this.timeZone).format('YYYY-MM-DD')
      }
    }
  },
  async mounted() {
    this.loading = true
    this.setPicaServiceId(this.picaServiceId)
    this.setCheckinId(this.$route.params.id)
    await this.fetchConfig({type: 'checkin', id: this.picaServiceId, scope: ConfigScope.PICA_SERVICE})
    await this.fetchCheckin()
    if (this.canAssignCoupon && this.checkin?.user) {
      await this.fetchRedemptions({ userId: this.checkin.user, picacode: this.checkin.pica_code })
    }
    this.loading = false
  },
  methods: {
    ...mapActions('login', [
      'fetchConfig'
    ]),
    ...mapActions('checkin', [
      'fetchCheckin',
      'setPicaServiceId',
      'setCheckinId',
    ]),
    ...mapActions('edgeCoupon', [
      'fetchRedemptions'
    ]),
    routeToSearchPhotos(picacode) {
      return {name: 'MediaSearch', params: {picaServiceId: this.picaServiceId}, query: {picacode: picacode}}
    },
    routeToBrowsePhotos(picacode) {
      return {name: 'photoSearch.search', params: {picaServiceId: this.picaServiceId}, query: {pica_code: picacode}}
    },
    copyPicaCode() {
      this.copyInClipboard(this.checkin.pica_code, 'Pica Code')
    },
    getOrderRoute(item) {
      return getDetailRouteParams(TRANSACTION_TYPES.ORDER, item.order_id)
    },
    getRefundRoute(refund) {
      return getDetailRouteParams(TRANSACTION_TYPES.REFUND, refund.id)
    },
    getOrderStatusColor(status) {
      const colors = {
        paid: 'success',
        refunded: 'danger',
        pending: 'warning',
        cancelled: 'secondary'
      }
      return colors[status] || 'primary'
    },
    editDateOfStay() {
      this.$refs.modalDateOfStay.open()
    },
    openAssignCouponModal() {
      this.$refs.modalAssignCoupon.open()
    },
    getRedemptionAmount(redemption) {
      if (redemption.effect_params_overrides?.amount !== undefined) {
        return redemption.effect_params_overrides.amount
      }
      return redemption.coupon?.batch?.promotion_rule?.effect_params?.amount || null
    }
  }
}
</script>

<style scoped>
.order-item-row {
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #ebedef;
}

.order-item-row:last-child {
  border-bottom: none;
}

.refund-row {
  padding: 0.35rem 0.5rem;
  background-color: #fdf2f2;
  border-radius: 4px;
  margin-top: 0.25rem;
}
</style>